import React, { useState, useEffect } from "react";
import { io } from "socket.io-client";
import { sendRequest } from "../api";
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Progress } from "@/components/ui/Progress";
import { GradientButton } from '@/components/ui/GradientButton';
import MazeGame from './MazeGame';
import { 
  Archive, 
  Download,
  Bell,
  X,
  ArrowLeft,
  ExternalLink
} from 'lucide-react';

export const ProcessingModal = ({ 
  downloadId, 
  onClose, 
  user,
  hideGame = false,
  customTitle,
  customSubtitles = [],
  isCrossPost = false
}) => {
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(5);
  const [videoUrl, setVideoUrl] = useState(null);
  const [socket, setSocket] = useState(null);
  const [polling, setPolling] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [notified, setNotified] = useState(false);
  const [completedVideos, setCompletedVideos] = useState(0);
  const [videosCount, setVideosCount] = useState(0);
  const [level, setLevel] = useState(1);
  const [failedCount, setFailedCount] = useState(0);
  const [showFailures, setShowFailures] = useState(false);
  const [failures, setFailures] = useState([]);

  // Reset all states when modal closes
  useEffect(() => {
    if (!downloadId) {
      setLoading(true);
      setProgress(5);
      setVideoUrl(null);
      setPolling(false);
      setErrorMessage(null);
      setNotified(false);
      setCompletedVideos(0);
      setVideosCount(0);
      setLevel(1);
      setFailedCount(0);
      setShowFailures(false);
      setFailures([]);
      
      // Cleanup socket connection
      if (socket) {
        socket.off("message", handleEvent);
        socket.disconnect();
        setSocket(null);
      }
    } else {
      // Reset failure states when starting a new download
      setFailedCount(0);
      setShowFailures(false);
      setFailures([]);
    }
  }, [downloadId]);

  // Socket event handler
  const handleEvent = (data) => {
    if (downloadId && data && data.startsWith(downloadId)) {
      if (data.includes('-failures-')) {
        const [_, __, count] = data.split('-');
        const failedCount = parseInt(count);
        setFailedCount(failedCount);
      } else {
        const [_, progress, completed, total] = data.split('-');
        setProgress(parseInt(progress));
        if (completed && total > 0) {
          setCompletedVideos(parseInt(completed));
          setVideosCount(parseInt(total));
        }
        if (progress === "100") {
          setPolling(true);
        }
      }
    }
  };

  // Fetch failures when failedCount changes
  useEffect(() => {
    if (failedCount > 0) {
      fetchFailures();
    }
  }, [failedCount]);

  const fetchFailures = async () => {
    try {
      const { failed_video_urls } = await sendRequest(`/downloads/${downloadId}/failures`);
      if (failed_video_urls) {
        setFailures(failed_video_urls.map(f => {
          try {
            return JSON.parse(f);
          } catch (e) {
            console.error('Error parsing failure:', e);
            return [f, 'Unknown error'];
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching failures:', error);
      setErrorMessage(error.message);
    }
  };

  // Socket connection
  useEffect(() => {
    if (!socket && !polling && downloadId) {
      const newSocket = io(process.env.WEBSOCKET_URL);
      setSocket(newSocket);
      newSocket.on("message", handleEvent);
    }

    return () => {
      if (socket) {
        socket.off("message", handleEvent);
        socket.disconnect();
        setSocket(null);
      }
    };
  }, [downloadId]);

  // Polling logic
  useEffect(() => {
    if (polling) {
      const interval = setInterval(async () => {
        try {
          const { zip_url } = await sendRequest(`/downloads/${downloadId}`);
          if (zip_url) {
            setVideoUrl(zip_url);
            clearInterval(interval);
            setLoading(false);
            setPolling(false);
            setProgress(100);
          }
        } catch (error) {
          if (error.status === 401) {
            window.location.reload()
          }
          setErrorMessage(error.message);
          setLoading(false)
          setPolling(false);
        } 
      }, 2500);

      return () => clearInterval(interval);
    }
  }, [polling]);

  const handleFinalDownload = () => {
    if (videoUrl) {
      window.open(videoUrl);
      onClose?.();
    }
  };

  const handleNotify = async () => {
    try {
      await sendRequest(`/downloads/${downloadId}/notify`, 'POST');
      setNotified(true);
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  const getSubtitle = () => {
    if (customSubtitles.length > 0) {
      const index = Math.min(Math.floor(progress / (100 / customSubtitles.length)), customSubtitles.length - 1);
      return customSubtitles[index];
    }

    if (completedVideos === videosCount) {
      if (failedCount > 0 && failedCount === completedVideos) {
        return "All videos failed to download. Is your account private?";
      }
      return progress <= 5 ? "This may take a while..." : `${completedVideos - failedCount} videos downloaded. Preparing zip file...`;
    }
    return `${completedVideos}/${videosCount} HD videos downloaded...`;
  };

  const renderFailuresTable = () => (
    <div className="mt-4 border rounded-lg overflow-hidden">
      <div className="max-h-[300px] overflow-y-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">URL</th>
              <th className="px-4 py-2 text-left text-sm font-medium text-gray-500">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {failures.map(([url, reason], i) => (
              <tr key={i} className="bg-white">
                <td className="px-4 py-2 text-sm">
                  <a 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    {url.substring(0, 50)}...
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </td>
                <td className="px-4 py-2 text-sm text-gray-600">{reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <Dialog open={!!downloadId} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <style>
          {`
            @keyframes gradientFlow {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }

            .cta-button {
              background: linear-gradient(
                90deg,
                rgba(37, 99, 235, 1),
                rgba(147, 51, 234, 0.9),
                rgba(236, 72, 153, 0.95)
              );
              background-size: 200% 100%;
              animation: gradientFlow 4s infinite;
              transition: all 0.3s ease;
            }

            .cta-button:hover {
              transform: translateY(-1px);
              box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
            }
          `}
        </style>

        <div className="text-center space-y-4 py-6">
          <div className="flex flex-col items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              {notified ? (
                <Bell className="h-6 w-6 text-green-500" />
              ) : (
                <Archive className="h-6 w-6 text-green-500 animate-bounce" />
              )}
            </div>
            <div className="w-full">
              <DialogTitle className={`text-xl font-bold mb-2 text-black`}>
                {notified ? "We'll notify you!" : (videoUrl ? 'Your Videos Are Ready!' : customTitle || 'Preparing Your HD Download')}
              </DialogTitle>
              
              {notified && <p className="text-gray-600 mb-4">We'll send you an email at {user?.email} when your videos are ready.</p>}

              {!videoUrl && progress > 0 && (
                <>
                  <p className="text-sm text-gray-500 mb-2 flex items-center justify-center gap-2">
                    {getSubtitle()}
                  </p>
                  {progress > 0 && <Progress value={progress} className="h-3 mb-4" />}
                </>
              )}

              {!videoUrl && progress === 0 && (
                <p className="text-gray-600 text-sm mb-4">We're downloading your videos in HD quality...</p>
              )}

              <div className="space-y-2">
                {videoUrl && !isCrossPost ? (
                  <GradientButton 
                    onClick={handleFinalDownload}
                    className="rounded-full w-full"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Now
                  </GradientButton>
                ) : user?.email && !notified && !isCrossPost && (
                  <Button 
                    variant="default"
                    className="w-full bg-black hover:bg-gray-800"
                    onClick={handleNotify}
                  >
                    <Bell className="h-4 w-4 mr-2" />
                    Notify me when ready
                  </Button>
                )}

                {notified && !videoUrl && (
                  <Button 
                    variant="default"
                    className="w-full bg-black hover:bg-gray-800"
                    onClick={onClose}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Close
                  </Button>
                )}

                {errorMessage && (
                  <p className="text-red-600">{errorMessage}</p>
                )}

                {failedCount > 0 && (
                  <div className="text-red-600">
                    <p>{failedCount} videos failed.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {failedCount > 0 ? (
            renderFailuresTable()
          ) : (
            !notified && !hideGame && (
              <div className="flex items-center justify-center">
                <div className="w-full border rounded-lg p-2 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-gray-600">Play while you wait!</p>
                    <span className="text-sm font-medium text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">Level {level}</span>
                  </div>
                  <MazeGame onLevelChange={setLevel} />
                </div>
              </div>
            )
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProcessingModal;